import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Select } from 'bs-unified-ui';
import {
  convertH2M,
  formatTimeString,
  USER_DEVICE_ZONE,
} from '~/utils/datetime';

const TimeSelect = ({
  placeholder,
  value,
  realTimeWithPresent,
  timezone = USER_DEVICE_ZONE,
  timeGap,
  inputFormat,
  name,
  timeOptionsRange,
  dataTestId,
  paperDataTestId,
  label,
  outputFormatLabel,
  ...props
}) => {
  const times = useMemo(() => {
    let loop = 0;
    let hour = 0;
    const arrSelectTimes = [];
    const timeOptionsRangeStartMin = timeOptionsRange.start
      ? convertH2M(moment(timeOptionsRange.start, 'hh:mm A').format('HH:mm'))
      : null;
    const timeOptionsRangeEndMin = timeOptionsRange.end
      ? convertH2M(moment(timeOptionsRange.end, 'hh:mm A').format('HH:mm'))
      : null;

    for (let i = 0; i <= (24 * 60) / timeGap; i += 1) {
      let tempLabel = `${
        hour.toLocaleString().length === 1 ? `0${hour}` : hour
      } : ${
        (loop * timeGap).toLocaleString().length === 1
          ? `0${loop * timeGap}`
          : loop * timeGap
      }`;
      if (hour * 60 + loop * timeGap === 1440) {
        tempLabel = '23:59';
      }
      arrSelectTimes.push({
        value: moment(tempLabel, 'HH:mm').format('hh:mm A'),
        value24h: moment(tempLabel, 'HH:mm').format('HH:mm'),
        label: moment(tempLabel, 'HH:mm').format(outputFormatLabel),
      });
      loop += 1;
      if (loop === 60 / timeGap) {
        hour += 1;
      }
      if (loop === 60 / timeGap) {
        loop = 0;
      }
    }
    return arrSelectTimes
      .filter((i) => {
        if (timeOptionsRangeStartMin) {
          return convertH2M(i.value24h) > timeOptionsRangeStartMin;
        }
        if (timeOptionsRangeEndMin) {
          return convertH2M(i.value24h) < timeOptionsRangeEndMin;
        }
        if (realTimeWithPresent) {
          const convertedValue = moment(
            moment(i.value, 'hh:mm A').format('HH:mm'),
            'HH:mm',
          );
          const h = convertedValue.hour();
          const m = convertedValue.minute();
          const now = moment().tz(timezone);
          return h > now.hour() || (h === now.hour() && m > now.minute());
        }
        return true;
      })
      .map((item) => ({ ...item, label: formatTimeString(item.label) }));
  }, [timeGap, realTimeWithPresent, timeOptionsRange, outputFormatLabel]);

  return (
    <Select
      data-testid={dataTestId}
      name={name}
      label={label}
      placeholder={placeholder}
      value={value ? moment(value, inputFormat).format('hh:mm A') : value || ''}
      options={times}
      {...props}
    />
  );
};

TimeSelect.defaultProps = {
  placeholder: 'Time',
  value: '',
  realTimeWithPresent: false,
  timezone: USER_DEVICE_ZONE,
  timeGap: 15,
  inputFormat: 'HH:mm',
  name: '',
  timeOptionsRange: {
    start: null,
    end: null,
  },
  dataTestId: '',
  paperDataTestId: '',
  label: '',
  outputFormatLabel: 'hh:mm A',
};
TimeSelect.propTypes = {
  placeholder: PropTypes.string,
  value: PropTypes.string,
  realTimeWithPresent: PropTypes.bool,
  timezone: PropTypes.string,
  timeGap: PropTypes.oneOf([10, 15, 20, 30]),
  inputFormat: PropTypes.string,
  name: PropTypes.string,
  timeOptionsRange: PropTypes.shape({
    start: PropTypes.string,
    end: PropTypes.string,
  }),
  dataTestId: PropTypes.string,
  paperDataTestId: PropTypes.string,
  label: PropTypes.string,
  outputFormatLabel: PropTypes.string,
};

export default TimeSelect;
